import { ReactNode } from 'react';
import { Theme } from '@material-ui/core';
import {
  UnifiedThemeProvider,
  createUnifiedTheme,
  createBaseThemeOptions,
  palettes,
} from '@backstage/theme';

const NAVIGATION_INDICATOR = '#FFA726';

// Paradigm Mark1 design tokens. Components read these through the theme
// (palette, overrides below), never as literals.
const MARK1 = {
  neutral50: '#F9F9FA',
  neutral100: '#F1F2F3',
  neutral200: '#E4E5E7',
  neutral300: '#9CA4B0',
  neutral500: '#5E6D82',
  neutral950: '#080A0C',
  primary: '#163B69',
  primaryDark: '#122D4E',
  infoSurface: '#E4EFFB',
  warning: '#FFDA44',
  gold: '#C99A00',
};
export const RADIUS = { md: 10, lg: 14, full: 9999 };
const SHADOW = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 1px 3px rgba(9, 9, 11, 0.15)',
};
// Inter, loaded in src/index.tsx. BUI components read --bui-font-regular,
// set to the same stack in src/global.css.
const UI_FONT = 'Inter, system-ui, sans-serif';

export const MONO_FONT =
  'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

// Dark mode greys, from the Catalog Dark mock: neutral greys (not navy), with
// a visible step between page, card, table header and inputs.
const DARK = {
  page: '#242424',
  card: '#2E2E2E',
  text: '#F5F5F5',
  textMuted: '#B8B8B8',
  textSubtle: '#8F8F8F',
  border: '#4A4A4A',
  link: '#8CBAF5',
  accent: '#9CC8FF',
  accentHover: '#B5D4FA',
  onAccent: '#0B1F3A',
  infoSurface: '#2F4A6E',
  hover: '#404040',
  gold: '#FFC21A',
};

/**
 * Surface colours the shared palette keys do not cover. Light mode repeats
 * the values these surfaces already had; dark mode uses the mock's greys.
 */
export type SurfacePalette = {
  tableHeader: string;
  tableHeaderText: string;
  tableHeaderBorder: string;
  tableRowOdd: string;
  tableRowEven: string;
  tableRowHover: string;
  tableRowDivider: string;
  tableBorder: string;
  tableFooter: string;
  input: string;
  inputBorder: string;
  inputPlaceholder: string;
  track: string;
  trackSelected: string;
  trackSelectedShadow: string;
  count: string;
  countText: string;
};

declare module '@material-ui/core/styles/createPalette' {
  interface Palette {
    surface?: SurfacePalette;
  }
  interface PaletteOptions {
    surface?: SurfacePalette;
  }
}

const LIGHT_SURFACE: SurfacePalette = {
  tableHeader: MARK1.neutral50,
  tableHeaderText: MARK1.neutral500,
  // The stock table header's border (MUI grey.A100).
  tableHeaderBorder: '#D5D5D5',
  // Backstage's stock table shades odd rows with background.default.
  tableRowOdd: MARK1.neutral100,
  tableRowEven: 'transparent',
  tableRowHover: MARK1.neutral50,
  // The stock table draws no row dividers.
  tableRowDivider: 'transparent',
  tableBorder: MARK1.neutral200,
  tableFooter: 'transparent',
  input: '#FFFFFF',
  inputBorder: MARK1.neutral200,
  inputPlaceholder: MARK1.neutral500,
  track: MARK1.neutral100,
  trackSelected: '#FFFFFF',
  trackSelectedShadow: SHADOW.sm,
  count: MARK1.neutral100,
  countText: MARK1.neutral500,
};

/**
 * The theme's surface colours. Falls back to the light ones under a theme
 * without them (Backstage's default theme in tests, for example).
 */
export const surfaceOf = (theme: { palette: { surface?: SurfacePalette } }) =>
  theme.palette.surface ?? LIGHT_SURFACE;

const DARK_SURFACE: SurfacePalette = {
  tableHeader: '#3D3D3D',
  tableHeaderText: '#D0D0D0',
  tableHeaderBorder: '#505050',
  tableRowOdd: DARK.card,
  tableRowEven: '#333333',
  tableRowHover: DARK.hover,
  tableRowDivider: '#444444',
  tableBorder: '#505050',
  tableFooter: '#383838',
  input: '#262626',
  inputBorder: '#5A5A5A',
  inputPlaceholder: '#9A9A9A',
  track: '#1F1F1F',
  trackSelected: '#454545',
  trackSelectedShadow: 'none',
  count: '#3F3F3F',
  countText: '#D6D6D6',
};

// Owner and Tag pickers (stock catalog-react autocompletes): 36px outlined
// field, radius 10, primary border and info surface once something is picked.
// Generated class names carry a suffix, so stock classes match by prefix.
const pickerInput = ({ theme }: { theme: any }) => ({
  minWidth: 160,
  // MUI's dense autocomplete padding is more specific than any theme rule.
  '& [class*="MuiOutlinedInput-root"]': {
    minHeight: 36,
    borderRadius: RADIUS.md,
    backgroundColor: theme.palette.surface.input,
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
  },
  // Dark mode only: a lighter outline than MUI's default, as in the mock.
  ...(theme.palette.type === 'dark' && {
    '& [class*="MuiOutlinedInput-notchedOutline"]': {
      borderColor: theme.palette.surface.inputBorder,
    },
  }),
  '& [class*="MuiOutlinedInput-root"] input': {
    height: 22,
    paddingTop: '7px !important',
    paddingBottom: '7px !important',
  },
  '&:has([class*="MuiAutocomplete-tag"]) [class*="MuiOutlinedInput-root"]': {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.infoBackground,
  },
  '&:has([class*="MuiAutocomplete-tag"]) [class*="MuiOutlinedInput-notchedOutline"]':
    {
      borderColor: theme.palette.primary.main,
    },
});

const componentOverrides = {
  MuiTextField: {
    defaultProps: {
      size: 'small' as const,
    },
  },
  MuiFormControl: {
    defaultProps: {
      size: 'small' as const,
    },
  },
  MuiSelect: {
    defaultProps: {
      size: 'small' as const,
    },
  },
  MuiButton: {
    defaultProps: {
      size: 'small' as const,
    },
    styleOverrides: {
      root: { borderRadius: RADIUS.md },
    },
  },
  MuiAutocomplete: {
    defaultProps: {
      size: 'small' as const,
    },
  },
  MuiChip: {
    defaultProps: {
      size: 'small' as const,
    },
  },
  MuiIconButton: {
    defaultProps: {
      size: 'small' as const,
    },
  },
  MuiToggleButton: {
    defaultProps: {
      size: 'small' as const,
    },
  },
  MuiToggleButtonGroup: {
    defaultProps: {
      size: 'small' as const,
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: { borderRadius: RADIUS.md },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: { borderRadius: RADIUS.lg },
      elevation1: { boxShadow: SHADOW.sm },
      elevation8: { boxShadow: SHADOW.md },
    },
  },
  // @backstage/core-components' <Select> renders a hardcoded BootstrapInput
  // that ignores MUI's `size` prop, so its padding must be overridden directly.
  BackstageSelectInputBase: {
    styleOverrides: {
      input: ({ theme }: { theme: any }) => ({
        padding: theme.spacing(0.5, 3.25, 0.5, 1.5),
        borderRadius: RADIUS.md,
        backgroundColor: theme.palette.surface.input,
      }),
    },
  },
  // Width and gutters of every standalone page (core-components <Content>),
  // mirroring @backstage/ui's <Container> so they line up with entity pages:
  // 120rem max, centred, 16px side padding and 20px from 640px wide. The
  // vertical padding is Content's own. Content's base rule steps its sides to
  // 24px at the same 600px media query, so that key is overridden too.
  BackstageContent: {
    styleOverrides: {
      root: {
        width: '100%',
        maxWidth: '120rem',
        marginInline: 'auto',
        paddingLeft: 16,
        paddingRight: 16,
        '@media (min-width:600px)': { paddingLeft: 16, paddingRight: 16 },
        '@media (min-width:640px)': { paddingLeft: 20, paddingRight: 20 },
      },
    },
  },
  // Starred star: Mark1 gold outline with the warning fill.
  BackstageFavoriteToggleIcon: {
    styleOverrides: {
      icon: ({ theme }: { theme: any }) => ({
        color: theme.palette.warning.main,
        '& svg': { stroke: theme.palette.gold, strokeWidth: 1 },
      }),
    },
  },
  // Catalog "All / Starred" as a segmented control (stock UserListPicker,
  // normally a side card): grey track, the selected option a white pill.
  // All is moved first with `order`.
  CatalogReactUserListPicker: {
    styleOverrides: {
      root: ({ theme }: { theme: any }) => ({
        // Block-level (not inline-flex) so the top margin collapses into the
        // margin of whatever sits above: space below the Actions page's search
        // field, no extra space under the catalog's kind tabs.
        display: 'flex',
        width: 'fit-content',
        flexDirection: 'row' as const,
        gap: 2,
        margin: theme.spacing(1.5, 0, 2),
        padding: 3,
        overflow: 'visible',
        borderRadius: RADIUS.md,
        boxShadow: 'none',
        backgroundColor: theme.palette.surface.track,
      }),
      title: { display: 'none' },
      groupWrapper: ({ theme }: { theme: any }) => ({
        display: 'block',
        margin: 0,
        overflow: 'visible',
        borderRadius: 0,
        boxShadow: 'none',
        backgroundColor: 'transparent',
        '&:last-child': { order: -1 },
        '& ul': { display: 'flex', gap: 2, padding: 0 },
        // Generated class names carry a suffix, so match by prefix.
        '& [class*="MuiListItem-container"]': {
          display: 'flex',
          alignItems: 'center',
          borderRadius: 8,
        },
        '& [class*="MuiListItem-container"]:has(.Mui-selected)': {
          backgroundColor: theme.palette.surface.trackSelected,
          boxShadow: theme.palette.surface.trackSelectedShadow,
        },
        '& [class*="MuiListItem-container"]:has(.Mui-selected) [class*="MuiListItemSecondaryAction-root"]':
          { color: theme.palette.text.primary },
        '& [class*="MuiListItemSecondaryAction-root"]': {
          position: 'static',
          transform: 'none',
          padding: theme.spacing(0, 1.5, 0, 1),
          fontFamily: MONO_FONT,
          fontSize: 12,
          lineHeight: '16px',
          fontWeight: 500,
          color: theme.palette.text.secondary,
        },
      }),
      listIcon: { display: 'none' },
      menuItem: ({ theme }: { theme: any }) => ({
        minHeight: 'auto',
        padding: '5px 0 5px 12px',
        borderBottom: 'none',
        color: theme.palette.text.secondary,
        backgroundColor: 'transparent',
        '&[class*="MuiListItem-secondaryAction"]': { paddingRight: 0 },
        '& [class*="MuiListItemText-root"]': { margin: 0 },
        '& [class*="MuiTypography-root"]': {
          fontSize: 14,
          lineHeight: '20px',
          fontWeight: 500,
        },
        '&:hover, &.Mui-selected, &.Mui-selected:hover': {
          color: theme.palette.text.primary,
          backgroundColor: 'transparent',
        },
      }),
    },
  },
  // Catalog search: 320px, 36px, radius 10, primary focus ring.
  CatalogReactEntitySearchBar: {
    styleOverrides: {
      searchToolbar: { padding: 0, minHeight: 'auto' },
      input: ({ theme }: { theme: any }) => ({
        width: 320,
        maxWidth: '100%',
        height: 36,
        padding: theme.spacing(0, 1),
        border: `1px solid ${theme.palette.surface.inputBorder}`,
        borderRadius: RADIUS.md,
        backgroundColor: theme.palette.surface.input,
        '& input::placeholder': {
          color: theme.palette.surface.inputPlaceholder,
          opacity: 1,
        },
        '&::before, &::after': { display: 'none' },
        '&:focus-within': {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
        },
        '& .MuiInputAdornment-root': { color: theme.palette.text.secondary },
        // Hide the disabled clear button while the field is empty.
        '& button[aria-label="clear search"].Mui-disabled': {
          display: 'none',
        },
      }),
    },
  },
  // Generic pickers (Service group) have no input class key; style the root.
  CatalogReactEntityAutocompletePicker: {
    styleOverrides: {
      root: pickerInput,
    },
  },
  CatalogReactEntityOwnerPicker: {
    styleOverrides: {
      input: pickerInput,
    },
  },
  CatalogReactEntityTagPicker: {
    styleOverrides: {
      input: pickerInput,
    },
  },
};

/**
 * Menu and autocomplete-popup rules for the catalog page only, as a nested
 * Material UI v4 theme (see CatalogIndexPage). They are not in the app theme
 * because that also feeds v5 components, which do not take these props, and
 * would restyle every other menu and autocomplete (scaffolder pickers).
 */
export const catalogPageTheme = (outer: Theme): Theme => {
  const overrides: any = outer.overrides ?? {};
  return {
    ...outer,
    props: {
      ...outer.props,
      // Select menus (rows per page, Type) open below their field with a
      // small gap instead of covering it (MUI v4's default).
      MuiMenu: {
        getContentAnchorEl: null,
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
      },
    },
    overrides: {
      ...overrides,
      MuiMenu: {
        ...overrides.MuiMenu,
        paper: { marginTop: 4, marginLeft: -8 },
      },
      MuiAutocomplete: {
        ...overrides.MuiAutocomplete,
        // MUI pins the menu to the field's width, which wraps longer
        // options. Size it to its options instead, never narrower than a
        // filter field and never wider than the screen (Popper keeps it on
        // screen). !important: the width is set inline.
        popper: {
          width: 'max-content !important',
          minWidth: 160,
          maxWidth: 'calc(100vw - 32px)',
        },
      },
    },
  };
};

export const lightTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    fontFamily: UI_FONT,
    palette: {
      ...palettes.light,
      primary: {
        main: MARK1.primary,
        dark: MARK1.primaryDark,
        contrastText: MARK1.neutral50,
      },
      warning: { main: MARK1.warning },
      background: { default: MARK1.neutral100, paper: '#FFFFFF' },
      text: {
        primary: MARK1.neutral950,
        secondary: MARK1.neutral500,
        disabled: MARK1.neutral300,
      },
      action: { hover: MARK1.neutral50 },
      divider: MARK1.neutral200,
      border: MARK1.neutral200,
      textSubtle: MARK1.neutral500,
      infoBackground: MARK1.infoSurface,
      gold: MARK1.gold,
      surface: LIGHT_SURFACE,
      navigation: {
        ...palettes.light.navigation,
        background: '#202733',
        indicator: NAVIGATION_INDICATOR,
      },
    },
  }),
  components: componentOverrides,
});

export const darkTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    fontFamily: UI_FONT,
    palette: {
      ...palettes.dark,
      // Neutral greys from the Catalog Dark mock, applied app-wide.
      primary: {
        main: DARK.accent,
        dark: DARK.accentHover,
        contrastText: DARK.onAccent,
      },
      warning: { main: MARK1.warning },
      background: { default: DARK.page, paper: DARK.card },
      text: {
        primary: DARK.text,
        secondary: DARK.textMuted,
        disabled: DARK.textSubtle,
      },
      action: { hover: DARK.hover },
      divider: DARK.border,
      border: DARK.border,
      textSubtle: DARK.textSubtle,
      link: DARK.link,
      linkHover: DARK.accentHover,
      infoBackground: DARK.infoSurface,
      gold: DARK.gold,
      surface: DARK_SURFACE,
      navigation: {
        ...palettes.dark.navigation,
        indicator: NAVIGATION_INDICATOR,
      },
    },
  }),
  components: componentOverrides,
});

export const CustomLightThemeProvider = ({
  children,
}: {
  children: ReactNode;
}) => <UnifiedThemeProvider theme={lightTheme} children={children} />;

export const CustomDarkThemeProvider = ({
  children,
}: {
  children: ReactNode;
}) => <UnifiedThemeProvider theme={darkTheme} children={children} />;
